import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Building2 } from "lucide-react";

/**
 * BranchSelector
 * Presents a dropdown of branches (fetched from `/api/branches`) plus an
 * "All branches" option. Emits `onChange(branch_id | "", branchObj | null)`.
 *
 * <BranchSelector value={branchId} onChange={setBranchId} allowAll includeOnline />
 */
export const BranchSelector = ({
  value,
  onChange,
  allowAll = true,
  includeOnline = true,
  className = "",
  testId = "branch-selector",
}) => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/branches`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        let list = res.data || [];
        if (!includeOnline) list = list.filter((b) => !b.is_online);
        setBranches(list);
      } catch {
        // silent — the selector just stays empty
      } finally {
        setLoading(false);
      }
    })();
  }, [includeOnline]);

  if (loading || branches.length === 0) return null;

  const handleChange = (v) => {
    const id = v === "__all__" ? "" : v;
    const obj = branches.find((b) => b.id === id) || null;
    onChange(id, obj);
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Building2 className="w-4 h-4 text-[#8B5CF6]" />
      <Select value={value || "__all__"} onValueChange={handleChange}>
        <SelectTrigger
          className="w-[220px] bg-white border-[#FECDD3]"
          data-testid={testId}
        >
          <SelectValue placeholder="Sucursal" />
        </SelectTrigger>
        <SelectContent>
          {allowAll && (
            <SelectItem value="__all__" data-testid={`${testId}-all`}>
              Todas las sucursales
            </SelectItem>
          )}
          {branches.map((b) => (
            <SelectItem key={b.id} value={b.id} data-testid={`${testId}-${b.id}`}>
              <span className="inline-flex items-center gap-2">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ backgroundColor: b.color }}
                />
                {b.name}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

/**
 * BranchBadge — pill-style indicator to render inline next to a page title.
 * <BranchBadge branch={branchObj} />
 */
export const BranchBadge = ({ branch }) => {
  if (!branch) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
      style={{
        color: branch.color,
        borderColor: `${branch.color}55`,
        backgroundColor: `${branch.color}12`,
      }}
      data-testid="branch-badge"
    >
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: branch.color }} />
      Viendo: {branch.name}
    </span>
  );
};

export default BranchSelector;
